/**
 * Client-side intent handler registry.
 *
 * Each handler implements an F-class intent's pure-client edit (no AI cost,
 * no worker round-trip). Handlers are wired here by H-031 (creator-client-tools-v07).
 *
 * Handler signature:
 *   async function handler({ image, config, intent }) {
 *     // image:  source image URL or data: URL
 *     // config: parsed intent.client_tool_config JSON
 *     // intent: full intent card object
 *     // returns: {
 *     //   result_image_url?: string,   // for stored R2 URLs
 *     //   result_data_url?:  string,   // for inline canvas exports
 *     //   metadata: Object             // tool-specific (crop bbox, color palette, etc.)
 *     // }
 *   }
 *
 * Keys MUST match `client_handler` values in DISPATCH_MATRIX (execution-mode-matrix.js):
 *   clientCanvasHandler, clientSvgHandler, clientTextHandler,
 *   cachedTemplateHandler, clientPixelHandler.
 */

/** @type {Record<string, (input: { image: string, config: Object, intent: Object }) => Promise<{ result_image_url?: string, result_data_url?: string, metadata?: Object }>>} */
export const clientHandlers = {}

/**
 * Register a handler at runtime. Generally handlers self-register at
 * import time; this helper exists for unit tests + late-bound modules.
 *
 * @param {string} handlerName
 * @param {Function} handler
 */
export function registerClientHandler(handlerName, handler) {
  const isHandlerObject =
    handler !== null &&
    typeof handler === 'object' &&
    typeof handler.run === 'function' &&
    (typeof handler.mountControls === 'function' || handler.mountControls === undefined)

  if (typeof handler !== 'function' && !isHandlerObject) {
    throw new Error(
      `registerClientHandler: '${handlerName}' must be a function or handler object with run (mountControls optional)`
    )
  }
  clientHandlers[handlerName] = handler
}

export function listRegisteredHandlers() {
  return Object.keys(clientHandlers)
}
